import type { Middleware } from '@reduxjs/toolkit';
import { api } from '@/shared/api';
import {
  configureAnalytics,
  hasAnalyticsUser,
  resetAnalyticsUser,
  setAnalyticsContext,
  setAnalyticsUser,
  track,
  type AnalyticsContext,
} from '@/shared/analytics';

// RootState и AppDispatch тут не годятся: store типизируется через свои middleware
type Dispatch = (action: unknown) => unknown;

interface AnalyticsState {
  login: { isLoggedIn: boolean };
  region: { regionFias: string | null };
}

export function analyticsContextFrom(state: AnalyticsState): AnalyticsContext {
  return {
    auth_state: state.login.isLoggedIn ? 'authorized' : 'guest',
    region_code: state.region.regionFias ?? undefined,
  };
}

// Вход сейчас один — через Госуслуги, и в PWA, и в MAX
const AUTH_METHOD = 'esia';

function trackAuthCompleted() {
  track('auth_completed', { method: AUTH_METHOD, result: 'success' });
}

/**
 * Вход случился, но auth_completed ещё не отправлен: по каталогу событие идёт
 * сразу после setUserId, а analyticsId приходит отдельным запросом профиля
 */
let awaitingAuthCompleted = false;

/**
 * Держит контекст аналитики в актуальном состоянии и привязывает визит
 * к пользователю. analyticsId приходит в /users/current; после свежего входа
 * его никто не запрашивает, поэтому запрос делаем здесь
 */
export const analyticsMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    const wasLoggedIn = (storeApi.getState() as AnalyticsState).login
      .isLoggedIn;
    const result = next(action);
    const state = storeApi.getState() as AnalyticsState;

    setAnalyticsContext(analyticsContextFrom(state));

    if (api.endpoints.getSettings.matchFulfilled(action)) {
      configureAnalytics(action.payload.analytics);
    }

    if (api.endpoints.getCurrentUser.matchFulfilled(action)) {
      setAnalyticsUser(action.payload.analyticsId);

      if (awaitingAuthCompleted) {
        awaitingAuthCompleted = false;
        trackAuthCompleted();
      }
    }

    // Профиль не загрузился — вход всё равно состоялся, событие уходит без User ID
    if (
      api.endpoints.getCurrentUser.matchRejected(action) &&
      !action.meta.condition &&
      awaitingAuthCompleted
    ) {
      awaitingAuthCompleted = false;
      trackAuthCompleted();
    }

    if (!wasLoggedIn && state.login.isLoggedIn) {
      if (hasAnalyticsUser()) {
        trackAuthCompleted();
      } else {
        awaitingAuthCompleted = true;
        void (storeApi.dispatch as Dispatch)(
          api.endpoints.getCurrentUser.initiate(undefined, {
            subscribe: false,
            forceRefetch: true,
          })
        );
      }
    }

    if (wasLoggedIn && !state.login.isLoggedIn) {
      awaitingAuthCompleted = false;
      resetAnalyticsUser();
    }

    return result;
  };
